import { useAppContext } from '@contexts/app';
import { reportAuthError } from '@services/auth/report';
import { useGetAdmin } from '@services/panel/admins/get';
import { useGetUser } from '@services/resources/users/old/get';
import { uuidRegEx } from '@utils/consts';
import { getUserData } from '@utils/getUserData';
import { useEffect, useState } from 'react';

const MISSING_ID_GRACE_MS = 3000;

export const useAccountLoad = (type: 'admin' | 'user', hasSession: boolean) => {
  const { isResolvingAuth } = useAppContext();
  const adminId = getUserData('admin').id;
  const userId = getUserData('user').id;

  const admin = useGetAdmin(adminId);
  const user = useGetUser(userId);

  const query = type === 'admin' ? admin : user;
  const id = type === 'admin' ? adminId : userId;
  const hasValidId = Boolean(id && uuidRegEx.test(id));

  const idMissing = hasSession && !hasValidId && !isResolvingAuth;
  const [missingId, setMissingId] = useState(false);

  useEffect(() => {
    if (!idMissing) {
      setMissingId(false);
      return;
    }
    const timeout = window.setTimeout(() => setMissingId(true), MISSING_ID_GRACE_MS);
    return () => window.clearTimeout(timeout);
  }, [idMissing]);

  const requestFailed = Boolean(query.error) && !query.isFetching;
  const failed = missingId || requestFailed;

  useEffect(() => {
    if (missingId) reportAuthError('missingSessionId', new Error(`Missing ${type} id in session`));
  }, [missingId, type]);

  useEffect(() => {
    if (requestFailed) reportAuthError(type === 'admin' ? 'loadAdmin' : 'loadUser', query.error);
  }, [requestFailed, type, query.error]);

  return {
    failed,
    canRetry: hasValidId,
    retry: () => void query.refetch(),
  };
};
